import React, { useMemo, useState } from 'react';
import './ProductCategoryForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';

// Categories at/under `initial` can't be picked as its own parent (would create a cycle)
function getSelfAndDescendantIds(categories, id) {
  const ids = new Set([id]);
  const idx = categories.findIndex((c) => c.id === id);
  if (idx === -1) return ids;
  const nodeDepth = categories[idx].depth;
  for (let i = idx + 1; i < categories.length; i++) {
    if (categories[i].depth <= nodeDepth) break;
    ids.add(categories[i].id);
  }
  return ids;
}

function ProductCategoryForm({ initial, categories, onSave, onCancel, loading }) {
  const [name, setName] = useState(initial?.name || '');
  const [parentId, setParentId] = useState(initial?.parent_id ? String(initial.parent_id) : '');
  const [description, setDescription] = useState(initial?.description || '');
  const [error, setError] = useState('');

  const isDefaultRow = !!initial?.is_system_default;

  const excludedIds = useMemo(
    () => (initial ? getSelfAndDescendantIds(categories, initial.id) : new Set()),
    [initial, categories]
  );

  const parentOptions = categories.filter((c) => c.is_active && !excludedIds.has(c.id));

  const validate = () => {
    if (!name.trim()) return 'Category name is required.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      name: name.trim(),
      parent_id: parentId ? Number(parentId) : null,
      description: description.trim(),
    });
  };

  return (
    <form className="product-category-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Category Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g., Hand Tools"
          />
        </div>
        <div>
          <label>Parent Category</label><br />
          <select
            className="styled-input"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            disabled={isDefaultRow}
            title={isDefaultRow ? 'The default Uncategorised category must stay at the top level' : undefined}
          >
            <option value="">(none — top level)</option>
            {parentOptions.map((c) => (
              <option key={c.id} value={c.id}>{'\u00A0\u00A0\u00A0'.repeat(c.depth)}{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Description</label><br />
        <textarea
          className="styled-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          style={{ width: '100%', maxWidth: 480, resize: 'vertical' }}
          placeholder="Optional. Appears beneath this Category's heading on the Extended Pricelist."
        />
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default ProductCategoryForm;
